/**
 * Billing is NOT implemented. There is no payment provider anywhere in this
 * codebase -- no Stripe, PayPal, Braintree, Paddle or anything else.
 *
 * api/services/subscription.py change_subscription() validates the tier NAME
 * and then writes it straight to app.Users. Its payment_id argument is stored
 * verbatim and never checked against anything, and schemas.py makes it
 * Optional with a default of None -- so any signed-in user can PUT
 * {"new_tier": "AI"} and receive the top tier for free. See task #182.
 *
 * There is no risk of charging anyone by accident, because there is no
 * payment path to misfire. The risk runs the other way: the UI advertised
 * "$9.99/month" and "Upgrade — $7/mo" (two different prices, which is its own
 * warning sign) next to a button that silently granted the tier. A tester who
 * clicks that reasonably believes they have entered into a paid arrangement.
 * Misleading someone about a commercial relationship is a trust problem, and
 * potentially a consumer-protection one, so while billing is unimplemented
 * the UI must not imply it exists.
 *
 * Set BILLING_ENABLED to true when #182 lands and real prices should show
 * again. Everything gated on it reverts to commercial copy in one edit.
 */
export const BILLING_ENABLED = false;

/** Shown wherever a plan change is offered, while billing is unimplemented. */
export const BILLING_DISABLED_NOTICE =
  "Billing isn't set up yet. Plan changes are free, instant and reversible during testing — no card is collected and nothing is charged.";

/** Verb for changing plans: "Upgrade" implies a purchase that cannot happen. */
export const planChangeVerb = () => (BILLING_ENABLED ? "Upgrade" : "Switch");

/**
 * Whether to ADVERTISE founding-member pricing (#330).
 *
 * Separate from BILLING_ENABLED on purpose, even though it is false for
 * the same reason today. A founding price is not a price, it is a
 * THREE-YEAR COMMITMENT: $1 off, held for three years from signup, with
 * a 120-day grace on any lapse. Showing that from a system with no
 * payment path is worse than showing a price next to a free switch --
 * a tester who takes it up believes they have locked something in.
 *
 * It is separate because the two will not necessarily switch on
 * together. Billing could ship without the founding offer running, and
 * the offer is a launch-window campaign that ends while billing carries
 * on. One flag would force those two decisions to be the same one.
 *
 * The API sends founding prices regardless; this decides whether anybody
 * is shown them. See api/services/subscription.py for the pricing itself
 * and why the discount is a flat dollar rather than a percentage.
 */
export const FOUNDING_OFFER_LIVE = false;

/**
 * Never the word "lifetime". Users read it as unconditional, so every
 * condition becomes fine print they feel tricked by -- and this offer
 * has real conditions. This wording is honest, needs no asterisk, and
 * survives being quoted back at you in a Facebook group.
 */
export const FOUNDING_TAGLINE =
  "Founding member price — yours for as long as you keep your subscription.";
